import { useSearchParams } from 'react-router';
import { FolderGit2, ScrollText, Search, UserRound, X } from 'lucide-react';
import { useProjects } from '../api/queries';
import { useUser } from '../api/user';
import { IconChip, PageHeader } from '../components/PageHeader';
import { useDebounced } from '../hooks/useDebounced';
import { AuditLog } from './audit/AuditLog';

const ACTION_PREFIXES = ['deploy', 'config', 'secret', 'file', 'environment', 'target', 'data', 'backup', 'vps', 'shared', 'settings', 'auth', 'job', 'project'];

export default function AuditPage() {
  const user = useUser();
  const [params, setParams] = useSearchParams();
  const projects = useProjects();
  const project = params.get('project') ?? '';
  const action = params.get('action') ?? '';
  const actor = params.get('actor') ?? '';
  const debouncedAction = useDebounced(action, 400);
  const debouncedActor = useDebounced(actor, 400);

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Audit log"
        icon={<IconChip icon={ScrollText} tone="secondary" size="lg" />}
        subtitle={user.platformAdmin ? 'All recorded actions across the platform.' : 'Recorded actions in the projects you can access. Platform-level events are visible to administrators.'}
      />
      <form className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end" onSubmit={(e) => e.preventDefault()} aria-label="Filter audit events">
        <fieldset className="fieldset py-0">
          <legend className="fieldset-legend pt-0 text-xs text-base-content/55">Project</legend>
          <label className="select select-sm w-full sm:w-52">
            <FolderGit2 className="size-3.5 opacity-50" aria-hidden="true" />
            <select value={project} onChange={(e) => set('project', e.target.value)}>
              <option value="">{user.platformAdmin ? 'All (including platform)' : 'All my projects'}</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.key}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
        <fieldset className="fieldset py-0">
          <legend className="fieldset-legend pt-0 text-xs text-base-content/55">Action starts with</legend>
          <label className="input input-sm w-full sm:w-52">
            <Search className="size-3.5 opacity-50" aria-hidden="true" />
            <input type="search" data-page-search className="grow font-mono" list="audit-actions" value={action} onChange={(e) => set('action', e.target.value)} placeholder="e.g. secret." />
            <kbd className="kbd kbd-xs max-sm:hidden">/</kbd>
          </label>
          <datalist id="audit-actions">
            {ACTION_PREFIXES.map((a) => (
              <option key={a} value={`${a}.`} />
            ))}
          </datalist>
        </fieldset>
        <fieldset className="fieldset py-0">
          <legend className="fieldset-legend pt-0 text-xs text-base-content/55">Actor (username)</legend>
          <label className="input input-sm w-full sm:w-48">
            <UserRound className="size-3.5 opacity-50" aria-hidden="true" />
            <input className="grow" value={actor} onChange={(e) => set('actor', e.target.value.trim())} placeholder="e.g. alice" />
          </label>
        </fieldset>
        {project || action || actor ? (
          <button type="button" className="btn btn-ghost btn-sm animate-pop self-start sm:self-auto" onClick={() => setParams({}, { replace: true })}>
            <X className="size-3.5" aria-hidden="true" />
            Clear filters
          </button>
        ) : null}
      </form>
      <AuditLog filters={{ project: project || undefined, action: debouncedAction || undefined, actor: debouncedActor || undefined }} />
    </>
  );
}
